import React, { useState, useEffect, useCallback } from 'react';
import { fetchScrapeLogs, fetchScheduleToday, triggerScrape, triggerTicketScan, cancelAllScrapes, getDebugScreenshotUrl, getDebugScreenshotTicketsUrl, fetchDebugScreenshots, getDebugScreenshotFileUrl, clearDebugScreenshots, fetchLevMissingSeatplans, fetchDebugLogs, fetchScreeningConflicts, fetchScreeningRepoints, revertScreeningRepoints, fetchImpossibleScreenings, fetchScreeningGap, probeHotCinemaEvents, startHotCinemaVerify, fetchHotCinemaVerifyStatus, fetchNewBranches, updateCinema, probeBranchSiteCode, scanChainBranches, adoptBranchNow } from '../api/client';
import { NEU_BG, NEU_RAISED, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, BORDER_SUBTLE, ACCENT, COLOR_OK, COLOR_WARN, COLOR_DANGER, TEXT_FAINT, SURF_ALT } from '../theme';
import { useAuth } from '../context/AuthContext';
import HallSeatMap, { parseSeatId } from '../components/HallSeatMap';
import PendingBlockedApprovals from '../components/PendingBlockedApprovals';

const ISRAEL_TZ = 'Asia/Jerusalem';
const formatIsraelDateTime = (value) => (
  value ? new Date(value).toLocaleString('he-IL', { timeZone: ISRAEL_TZ }) : '-'
);
const formatIsraelTime = (value) => (
  value ? new Date(value).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', timeZone: ISRAEL_TZ }) : '-'
);

const CHAINS = [
  { key: 'hot_cinema',   label: 'הוט סינמה',    color: '#dc2626' },
  { key: 'movieland',    label: 'מובילנד',       color: '#be123c' },
  { key: 'cinema_city',  label: 'סינמה סיטי',   color: '#7c3aed' },
  { key: 'planet',       label: 'פלאנט',         color: '#0369a1' },
  { key: 'rav_hen',           label: 'רב-חן',              color: '#0f766e' },
  { key: 'lev',               label: 'לב',                 color: '#92400e' },
  { key: 'cinematheque_tlv',      label: 'סינמטק תל אביב', color: '#4f46e5' },
  { key: 'cinematheque_jlm',      label: 'סינמטק ירושלים', color: '#7c3aed' },
  { key: 'cinematheque_herzliya', label: 'סינמטק הרצליה',  color: '#059669' },
  { key: 'cinematheque_holon',    label: 'סינמטק חולון',   color: '#db2777' },
  { key: 'cinematheque_haifa',    label: 'סינמטק חיפה',    color: '#0e7490' },
  { key: 'cinematheque_rosh_pina', label: 'סינמטק ראש פינה', color: '#ea580c' },
  { key: 'cinematheque_sderot',   label: 'סינמטק שדרות',   color: '#854d0e' },
];

function ChainCard({ chain, running, onScrape, onTickets }) {
  const { hasPermission } = useAuth();
  const isMe = running === chain.key;
  const busy = !!running;
  return (
    <div style={{ background: 'var(--surf-alt)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--brd)', transition: 'box-shadow .15s' }}>
      <div style={{ height: 3, background: chain.color }} />
      <div style={{ padding: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--txt)', marginBottom: 12 }}>{chain.label}</div>
        <div style={{ display: 'flex', gap: 7 }}>
          {hasPermission('scrape.trigger_full') && (
            <button onClick={() => onScrape(chain.key)} disabled={busy} style={{
              flex: 1, padding: '7px 0', borderRadius: 7, border: 'none',
              background: busy ? 'var(--faint)' : chain.color, color: '#fff',
              fontWeight: 700, fontSize: 12, fontFamily: 'Heebo,sans-serif',
              cursor: busy ? 'not-allowed' : 'pointer', opacity: busy && !isMe ? 0.45 : 1,
              transition: 'opacity .15s',
            }}>{isMe ? 'רץ...' : 'סרוק'}</button>
          )}
          {hasPermission('scrape.trigger_tickets') && (
            <button onClick={() => onTickets(chain.key)} disabled={busy} style={{
              padding: '7px 11px', borderRadius: 7, border: `1px solid ${chain.color}66`,
              background: 'transparent', color: chain.color,
              fontWeight: 600, fontSize: 12, fontFamily: 'Heebo,sans-serif',
              cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.45 : 1,
              transition: 'opacity .15s',
            }}>כיסאות</button>
          )}
        </div>
      </div>
    </div>
  );
}

// Two films cannot start in one hall at one moment. When the data says
// otherwise, one row is an artefact of a renamed or swapped listing and its
// tickets were being counted twice everywhere. The losing row is merged into
// the survivor and deleted, so this log is the only record left of it.
// Screenings that ended up on the wrong film. Until the booking_url lookup was
// scoped to a cinema and a showtime, a live screening could match a months-old
// row carrying a reused event id and drag it onto today's film while keeping
// the old date — so a film looked like it had been playing since before its
// release. Those rows are invisible in the normal views: they look like
// ordinary screenings of the wrong movie. This is how to find and undo them.
//
// The verdicts come from the same classifier the revert button acts on, so the
// count here and the count it changes are the same number.
function ScreeningRepointsPanel({ refreshNonce }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    fetchScreeningRepoints().then(setData).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load, refreshNonce]);

  if (!data || !data.total_repoints) return null;

  const cardStyle = {
    background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)',
    overflow: 'hidden',
  };
  const th = {
    padding: '8px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700,
    color: 'var(--faint)', borderBottom: '1px solid var(--brd)',
    background: 'var(--surf-alt)', whiteSpace: 'nowrap',
  };
  const td = { padding: '9px 12px', fontSize: 12, color: 'var(--txt)' };
  const suspect = data.suspect_screenings || 0;

  const run = (dryRun) => {
    setBusy(true);
    revertScreeningRepoints(dryRun)
      .then((res) => { setPreview(res); if (!dryRun) load(); })
      .catch((e) => setPreview({ error: e?.response?.data?.detail || 'הפעולה נכשלה' }))
      .finally(() => setBusy(false));
  };

  return (
    <div style={cardStyle}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', padding: '14px 20px', background: 'none', border: 'none',
          borderBottom: open ? '1px solid var(--brd)' : 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10, textAlign: 'right',
          fontFamily: 'Heebo, sans-serif',
        }}
      >
        <span style={{ fontSize: 18 }}>🎯</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--txt)' }}>
            הקרנות ששויכו לסרט אחר
            {suspect > 0 && (
              <span style={{ color: COLOR_DANGER, fontSize: 12, marginRight: 8 }}>
                · {suspect} לשחזור
              </span>
            )}
            {data.undetermined > 0 && (
              <span style={{ color: COLOR_WARN, fontSize: 12, marginRight: 8 }}>
                · {data.undetermined} לא הוכרעו
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            הכרעה לפי תקופת ההקרנות הנקייה של הסרט — הקרנות שהועברו אינן משמשות
            כראיה על עצמן. {data.legitimate || 0} שיוכים תקינים (שינוי שם) יישארו.
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--faint)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div>
          <div style={{ padding: '12px 20px', display: 'flex', gap: 10,
                        flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => run(true)}
              disabled={busy}
              style={{
                padding: '8px 16px', borderRadius: 10, border: '1px solid var(--brd)',
                background: 'var(--surf-alt)', color: 'var(--txt)', fontWeight: 700,
                fontSize: 12, cursor: busy ? 'default' : 'pointer',
              }}
            >
              {busy ? 'בודק...' : 'הצג מה יוחזר (ללא שינוי)'}
            </button>
            <button
              onClick={() => run(false)}
              disabled={busy || !preview || preview.error || !preview.reverted}
              style={{
                padding: '8px 16px', borderRadius: 10, border: 'none',
                background: (!preview || preview.error || !preview.reverted)
                  ? 'var(--surf-alt)' : COLOR_DANGER,
                color: (!preview || preview.error || !preview.reverted)
                  ? 'var(--faint)' : '#fff',
                fontWeight: 700, fontSize: 12,
                cursor: (busy || !preview || preview.error || !preview.reverted)
                  ? 'default' : 'pointer',
              }}
            >
              בצע שחזור
            </button>
            {data.settled > 0 && (
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                כבר שוחזרו: {data.settled}
              </span>
            )}
          </div>

          {preview && (
            <div style={{ padding: '0 20px 12px', fontSize: 12,
                          color: preview.error ? COLOR_DANGER : 'var(--muted)' }}>
              {preview.error
                ? preview.error
                : `${preview.dry_run ? 'יוחזרו' : 'הוחזרו'} ${preview.reverted} הקרנות · ` +
                  `${preview.kept} שיוכים תקינים יישארו · ` +
                  `${preview.undetermined} לא הוכרעו ולא ייגעו.`}
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={th}>הסרט שקיבל</th>
                  <th style={th}>לשחזור</th>
                  <th style={th}>לא הוכרעו</th>
                  <th style={th}>המועד המוקדם ביותר</th>
                  <th style={th}>סניפים</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {data.movies.map((m) => (
                  <React.Fragment key={m.movie_id}>
                    <tr style={{ borderBottom: '1px solid var(--faint)' }}>
                      <td style={td}>{m.title || `#${m.movie_id}`}</td>
                      <td style={{ ...td, color: m.harmful ? COLOR_DANGER : 'var(--faint)',
                                   fontWeight: m.harmful ? 700 : 400 }}>
                        {m.harmful}
                      </td>
                      <td style={{ ...td, color: m.undetermined ? COLOR_WARN : 'var(--faint)' }}>
                        {m.undetermined}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{m.earliest || '—'}</td>
                      <td style={{ ...td, fontSize: 11, color: 'var(--muted)' }}>
                        {(m.branches || []).join(', ') || '—'}
                      </td>
                      <td style={td}>
                        <button
                          onClick={() => setExpanded(expanded === m.movie_id ? null : m.movie_id)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: ACCENT, fontSize: 11, fontFamily: 'Heebo, sans-serif',
                          }}
                        >
                          {expanded === m.movie_id ? 'הסתר' : 'הצג הקרנות'}
                        </button>
                      </td>
                    </tr>
                    {expanded === m.movie_id && (
                      <tr>
                        <td colSpan={6} style={{ padding: '0 12px 12px', background: 'var(--surf-alt)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                              {(m.screenings || []).map((sc) => (
                                <tr key={sc.screening_id}>
                                  <td style={{ ...td, fontSize: 11, whiteSpace: 'nowrap' }}>
                                    {sc.showtime}
                                  </td>
                                  <td style={{ ...td, fontSize: 11 }}>{sc.cinema || '—'}</td>
                                  <td style={{ ...td, fontSize: 11, color: 'var(--muted)' }}>
                                    הגיעה מ־{sc.from_movie || '—'}
                                  </td>
                                  <td style={{ ...td, fontSize: 11,
                                               color: sc.verdict === 'harmful' ? COLOR_DANGER : COLOR_WARN }}>
                                    {sc.verdict === 'harmful' ? 'לשחזור' : 'לא הוכרע'}
                                    <span style={{ color: 'var(--faint)' }}>
                                      {' '}({sc.basis === 'native_run' ? 'מחוץ לתקופה'
                                        : sc.basis === 'release_date' ? 'לפני היציאה'
                                        : 'אין ראיה'})
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// The screenings that were never recorded at all. When a live screening
// matched an old row by a recycled event id, the caller updated that row
// instead of inserting one, so nothing exists at the real showtime — which is
// why restoring the film on the old rows left the weekend tables unchanged.
// There was nothing there to restore.
function ScreeningGapPanel({ refreshNonce }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState(null);

  useEffect(() => {
    fetchScreeningGap().then(setData).catch(() => setData(null));
  }, [refreshNonce]);

  if (!data || !data.window) return null;

  const th = {
    padding: '8px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700,
    color: 'var(--faint)', borderBottom: '1px solid var(--brd)',
    background: 'var(--surf-alt)', whiteSpace: 'nowrap',
  };
  const td = { padding: '9px 12px', fontSize: 12, color: 'var(--txt)' };

  return (
    <div style={{ background: 'var(--surf)', borderRadius: 16,
                  boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', padding: '14px 20px', background: 'none', border: 'none',
          borderBottom: open ? '1px solid var(--brd)' : 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10, textAlign: 'right',
          fontFamily: 'Heebo, sans-serif',
        }}
      >
        <span style={{ fontSize: 18 }}>🕳️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--txt)' }}>
            הקרנות שלא נרשמו כלל
            <span style={{ color: COLOR_DANGER, fontSize: 12, marginRight: 8 }}>
              · ~{data.estimated} חסרות
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            {data.window.start} עד {data.window.end} · הן לא הועברו למקום אחר —
            הן מעולם לא נוצרו, ולכן טבלאות הסופ״ש לא השתנו אחרי השחזור.
            {data.named > 0 && ` ${data.named} מהן עדיין ניתנות לזיהוי לפי מזהה האירוע.`}
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--faint)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div>
          <div style={{ padding: '12px 20px', display: 'flex', gap: 10,
                        flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => {
                setProbing(true);
                setProbe(null);
                probeHotCinemaEvents(3)
                  .then(setProbe)
                  .catch((e) => setProbe({ error: e?.response?.data?.detail || 'הבדיקה נכשלה' }))
                  .finally(() => setProbing(false));
              }}
              disabled={probing || !data.named}
              style={{
                padding: '8px 16px', borderRadius: 10, border: '1px solid var(--brd)',
                background: 'var(--surf-alt)', color: 'var(--txt)', fontWeight: 700,
                fontSize: 12, cursor: probing ? 'default' : 'pointer',
              }}
            >
              {probing ? 'בודק מול הוט סינמה...' : 'בדוק אם אפשר לשחזר'}
            </button>
            <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.7, flex: 1 }}>
              שולף את התשובה הגולמית של הוט סינמה עבור מזהי האירועים החסרים.
              <strong> אם אין שם תאריך — אי אפשר לשחזר</strong>, כי לא נדע לאיזה
              יום לשייך את ההקרנה.
            </div>
          </div>

          {probe && (
            <pre style={{
              margin: '0 20px 14px', padding: 12, borderRadius: 10,
              background: 'var(--surf-alt)', fontSize: 10, direction: 'ltr',
              textAlign: 'left', maxHeight: 320, overflow: 'auto',
              color: probe.error ? COLOR_DANGER : 'var(--txt)',
            }}>
              {probe.error ? probe.error : JSON.stringify(probe, null, 2)}
            </pre>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
              <thead>
                <tr>
                  <th style={th}>תאריך</th>
                  <th style={th}>נרשמו</th>
                  <th style={th}>צפוי (חציון אותו יום בשבוע)</th>
                  <th style={th}>חסרות</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_day || []).map((d) => (
                  <tr key={d.date} style={{ borderBottom: '1px solid var(--faint)' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{d.date}</td>
                    <td style={td}>{d.observed}</td>
                    <td style={{ ...td, color: 'var(--muted)' }}>{d.expected}</td>
                    <td style={{ ...td, color: d.missing ? COLOR_DANGER : 'var(--faint)',
                                 fontWeight: d.missing ? 700 : 400 }}>
                      {d.missing}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ padding: '14px 20px 18px' }}>
            <ChainVerifyPanel window={data.window} />
          </div>
        </div>
      )}
    </div>
  );
}


// Verify our rows against the chain's own record. The probe established that
// BiggerPicture answers for events months past with the showtime, film,
// branch, hall, capacity and seats sold — so instead of inferring from the
// repoint log what a row should say, we ask the source and repair from it.
function ChainVerifyPanel({ window: gapWindow }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  // Poll while a run is in flight. The run itself is a background task paced
  // to a time budget — the request that starts it returns immediately, so
  // nothing here holds a connection open for a quarter of an hour.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const tick = () => {
      fetchHotCinemaVerifyStatus()
        .then((s) => { if (alive) setStatus(s); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [open]);

  if (!gapWindow) return null;

  const th = {
    padding: '8px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700,
    color: 'var(--faint)', borderBottom: '1px solid var(--brd)',
    background: 'var(--surf-alt)', whiteSpace: 'nowrap',
  };
  const td = { padding: '9px 12px', fontSize: 12, color: 'var(--txt)' };

  const running = !!(status && status.running);
  const counts = (status && status.counts) || {};
  const done = !!(status && status.finished_at && !running);
  const pct = status && status.total
    ? Math.round((status.checked / status.total) * 100) : 0;
  const etaMin = running && status.total && status.interval
    ? Math.max(1, Math.round(((status.total - status.checked) * status.interval) / 60))
    : null;

  const LABELS = {
    movie: 'סרט שגוי', showtime: 'שעה שגויה', hall: 'אולם שגוי',
    capacity: 'קיבולת שגויה', tickets: 'כרטיסים שגויים',
    unknown_title: 'סרט שאינו אצלנו', labels_filled: 'תוויות שהושלמו',
    unreachable: 'לא נגישות', ok: 'תקינות',
  };

  const start = (dryRun) => {
    setError(null);
    startHotCinemaVerify(gapWindow.start, gapWindow.end, dryRun)
      .then((res) => { if (res.status === 'already_running') setStatus(res); })
      .catch((e) => setError(e?.response?.data?.detail || 'ההפעלה נכשלה'));
  };

  return (
    <div style={{ background: 'var(--surf)', borderRadius: 16,
                  boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', padding: '14px 20px', background: 'none', border: 'none',
          borderBottom: open ? '1px solid var(--brd)' : 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10, textAlign: 'right',
          fontFamily: 'Heebo, sans-serif',
        }}
      >
        <span style={{ fontSize: 18 }}>✅</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--txt)' }}>
            אימות מול הוט סינמה
            {running && (
              <span style={{ color: ACCENT, fontSize: 12, marginRight: 8 }}>
                · רץ ({pct}%)
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            הרשת מחזירה לכל אירוע את השעה, הסרט, הסניף, האולם, הקיבולת ומספר
            הכרטיסים — גם לחודשים אחורה. רץ ברקע בפולסים, כרבע שעה לכל החלון.
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--faint)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div>
          <div style={{ padding: '12px 20px', display: 'flex', gap: 10,
                        flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => start(true)}
              disabled={running}
              style={{
                padding: '8px 16px', borderRadius: 10, border: '1px solid var(--brd)',
                background: 'var(--surf-alt)', color: 'var(--txt)', fontWeight: 700,
                fontSize: 12, cursor: running ? 'default' : 'pointer',
              }}
            >
              הרץ אימות (ללא שינוי)
            </button>
            <button
              onClick={() => start(false)}
              disabled={running || !done || !status || !status.dry_run}
              style={{
                padding: '8px 16px', borderRadius: 10, border: 'none',
                background: (running || !done || !status || !status.dry_run)
                  ? 'var(--surf-alt)' : ACCENT,
                color: (running || !done || !status || !status.dry_run)
                  ? 'var(--faint)' : '#fff',
                fontWeight: 700, fontSize: 12,
                cursor: (running || !done || !status || !status.dry_run)
                  ? 'default' : 'pointer',
              }}
            >
              תקן מהרשת
            </button>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>
              {gapWindow.start} עד {gapWindow.end}
            </span>
          </div>

          {error && (
            <div style={{ padding: '0 20px 12px', fontSize: 12, color: COLOR_DANGER }}>
              {error}
            </div>
          )}

          {status && status.error && (
            <div style={{ padding: '0 20px 12px', fontSize: 12, color: COLOR_DANGER }}>
              {status.error}
            </div>
          )}

          {status && status.total > 0 && (
            <div style={{ padding: '0 20px 12px' }}>
              <div style={{ height: 8, borderRadius: 6, background: 'var(--surf-alt)',
                            overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 6, background: ACCENT,
                  width: `${pct}%`, transition: 'width .4s',
                }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
                {status.checked} מתוך {status.total}
                {running && etaMin && ` · עוד כ-${etaMin} דק'`}
                {done && (status.dry_run ? ' · הסתיים, לא בוצע שינוי' : ' · הסתיים, תוקן')}
              </div>
            </div>
          )}

          {status && status.counts && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8,
                          padding: '0 20px 14px' }}>
              {Object.entries(LABELS).map(([key, label]) => (
                <div key={key} style={{
                  padding: '6px 12px', borderRadius: 10, background: 'var(--surf-alt)',
                  fontSize: 11, color: 'var(--muted)',
                }}>
                  {label}:{' '}
                  <strong style={{
                    color: (counts[key] && !['ok', 'labels_filled'].includes(key))
                      ? COLOR_DANGER : 'var(--txt)',
                  }}>
                    {counts[key] || 0}
                  </strong>
                </div>
              ))}
            </div>
          )}

          {status && (status.samples || []).length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
                <thead>
                  <tr>
                    <th style={th}>הקרנה</th>
                    <th style={th}>הרשת אומרת</th>
                    <th style={th}>הפרשים</th>
                  </tr>
                </thead>
                <tbody>
                  {status.samples.map((sm) => (
                    <tr key={sm.screening_id} style={{ borderBottom: '1px solid var(--faint)' }}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        #{sm.screening_id}
                        <div style={{ fontSize: 10, color: TEXT_TERTIARY }}>
                          אירוע {sm.event_id}
                        </div>
                      </td>
                      <td style={{ ...td, fontSize: 11 }}>
                        {sm.chain_says.showtime} · {sm.chain_says.title}
                        <div style={{ color: 'var(--muted)' }}>
                          {sm.chain_says.branch} · {sm.chain_says.hall} ·{' '}
                          {sm.chain_says.sold}/{sm.chain_says.capacity}
                        </div>
                      </td>
                      <td style={{ ...td, fontSize: 11 }}>
                        {Object.entries(sm.changes).map(([k, v]) => (
                          <div key={k} style={{ color: 'var(--muted)' }}>
                            {LABELS[k] || k}: {v.from} ← {v.to}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Independent of the repoint log on purpose: replaying that log can only
// repair what it recorded, and a screening can land under the wrong film with
// nothing written down — two different films merged into one canonical family,
// for instance. A ticket cannot be sold for a screening that ran before the
// film came out, so this checks against TMDB rather than our own bookkeeping.
function NewBranchesPanel({ refreshNonce }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [edits, setEdits] = useState({});      // cinema_id -> {city, city_he, name_he}
  const [saving, setSaving] = useState(null);
  const [probe, setProbe] = useState({});      // theater_id -> result | 'loading'
  const [scan, setScan] = useState(null);      // live feed breakdown | 'loading'
  const [adopting, setAdopting] = useState(null);  // `${chain}-${id}` in flight
  const [adoptMsg, setAdoptMsg] = useState({});    // `${chain}-${id}` -> result

  const load = useCallback(() => {
    fetchNewBranches().then(setData).catch(() => setData(null));
  }, []);

  useEffect(() => { load(); }, [load, refreshNonce]);

  const pending = (data && data.pending_review) || [];
  const unmapped = (data && data.unmapped) || [];
  const merges = (data && data.duplicate_merges) || {};

  const field = (id, key, fallback) =>
    (edits[id] && edits[id][key] !== undefined) ? edits[id][key] : fallback;
  const setField = (id, key, value) =>
    setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [key]: value } }));

  const save = async (row) => {
    setSaving(row.cinema_id);
    try {
      await updateCinema(row.cinema_id, {
        name_he: field(row.cinema_id, 'name_he', row.name_he),
        city: field(row.cinema_id, 'city', row.city),
        city_he: field(row.cinema_id, 'city_he', row.city_he),
      });
      load();
    } finally {
      setSaving(null);
    }
  };

  const runScan = async () => {
    setScan('loading');
    try {
      setScan(await scanChainBranches());
    } catch {
      setScan({ error: 'הבדיקה נכשלה' });
    }
  };

  const adoptNow = async (chainKey, theaterId) => {
    const key = `${chainKey}-${theaterId}`;
    setAdopting(key);
    try {
      const res = await adoptBranchNow(chainKey, theaterId);
      setAdoptMsg((m) => ({ ...m, [key]: res }));
      if (res.adopted) { load(); runScan(); }
    } catch {
      setAdoptMsg((m) => ({ ...m, [key]: { error: 'האימוץ נכשל' } }));
    } finally {
      setAdopting(null);
    }
  };

  const runProbe = async (theaterId) => {
    setProbe((p) => ({ ...p, [theaterId]: 'loading' }));
    try {
      const res = await probeBranchSiteCode(theaterId);
      setProbe((p) => ({ ...p, [theaterId]: res }));
    } catch {
      setProbe((p) => ({ ...p, [theaterId]: { error: 'הבדיקה נכשלה' } }));
    }
  };

  const input = {
    background: 'var(--surf-alt)', border: '1px solid var(--brd)', borderRadius: 6,
    padding: '5px 9px', color: 'var(--txt)', fontSize: 12,
    fontFamily: 'Heebo, sans-serif', outline: 'none', width: '100%',
    boxSizing: 'border-box',
  };
  const total = pending.length + unmapped.length;

  return (
    <div style={{ background: 'var(--surf)', borderRadius: 16,
                  boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', padding: '14px 20px', background: 'none', border: 'none',
          borderBottom: open ? '1px solid var(--brd)' : 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10, textAlign: 'right',
          fontFamily: 'Heebo, sans-serif',
        }}
      >
        <span style={{ fontSize: 18 }}>🏢</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--txt)' }}>
            סניפים חדשים{total > 0 ? ` (${total})` : ''}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            {pending.length > 0 && `${pending.length} סניפים נקלטו מהרשת וממתינים לאישור העיר. `}
            {unmapped.length > 0 && `${unmapped.length} מזהי סניף לא ממופים — ההקרנות שלהם נזרקות. `}
            {total === 0 && 'לא נמצאו סניפים חדשים. אפשר לבדוק מול הרשתות עכשיו, בלי לחכות לסריקה.'}
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--faint)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ padding: '14px 20px 18px', display: 'flex',
                      flexDirection: 'column', gap: 18 }}>
          {/* Live check — independent of the scheduler and of JobRun, so
              "no new branch" and "no run has logged one" stay distinguishable. */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                          flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--txt)' }}>
                בדיקה מול הרשתות עכשיו
              </div>
              <button
                onClick={runScan}
                disabled={scan === 'loading'}
                style={{
                  marginInlineStart: 'auto', padding: '5px 16px', borderRadius: 7,
                  border: 'none', background: ACCENT, color: '#fff', fontWeight: 700,
                  fontSize: 12, cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
                }}
              >{scan === 'loading' ? 'בודק...' : 'בדוק עכשיו'}</button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
              שולף את לוח ההקרנות של מובילנד, סינמה סיטי והוט סינמה ומפרק אותו לפי מזהה
              סניף. סניף שמופיע כאן בלי שם הוא סניף שההקרנות שלו נזרקות.
            </div>
            {scan === 'loading' && (
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>שולף מהרשתות...</div>
            )}
            {scan && scan !== 'loading' && scan.error && (
              <div style={{ fontSize: 12, color: COLOR_DANGER }}>{scan.error}</div>
            )}
            {scan && scan !== 'loading' && scan.chains && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {scan.chains.map((c) => (
                  <div key={c.chain_key} style={{
                    border: '1px solid var(--brd)', borderRadius: 10, overflow: 'hidden',
                  }}>
                    <div style={{
                      padding: '8px 12px', background: 'var(--surf-alt)',
                      display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{c.chain}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {c.branches ? c.branches.length : 0} סניפים בפיד ·
                        {' '}{c.total_screenings || 0} הקרנות
                      </span>
                      {c.unmapped > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: COLOR_DANGER }}>
                          {c.unmapped} לא ממופים
                        </span>
                      )}
                      {!c.error && c.unmapped === 0 && (
                        <span style={{ fontSize: 11, color: COLOR_OK }}>הכול ממופה</span>
                      )}
                    </div>
                    {c.error ? (
                      <div style={{ padding: '10px 12px', fontSize: 12, color: COLOR_DANGER }}>
                        {c.error}
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                          <thead>
                            <tr>
                              {['TheaterId', 'סניף', 'הקרנות', 'ימים', 'טווח'].map((h) => (
                                <th key={h} style={{
                                  padding: '6px 12px', textAlign: 'right', fontSize: 10,
                                  fontWeight: 700, color: 'var(--faint)',
                                  borderBottom: '1px solid var(--brd)', whiteSpace: 'nowrap',
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(c.branches || []).map((b) => (
                              <tr key={b.theater_id ?? `loc-${b.location}`} style={{
                                borderBottom: '1px solid var(--faint)',
                                background: b.mapped_name ? 'transparent' : 'var(--warn-bg)',
                              }}>
                                <td style={{ padding: '7px 12px', fontSize: 12,
                                             fontFamily: 'monospace' }}>{b.theater_id ?? '—'}</td>
                                <td style={{ padding: '7px 12px', fontSize: 12 }}>
                                  {b.mapped_name && b.location && (
                                    <span style={{ color: 'var(--faint)', fontSize: 11, marginInlineEnd: 6 }}>
                                      {b.location} ←
                                    </span>
                                  )}
                                  {b.mapped_name || (
                                    <span>
                                      {b.resolved_name ? (
                                        <b style={{ color: 'var(--txt)' }}>{b.resolved_name}</b>
                                      ) : (
                                        <span style={{ color: COLOR_DANGER, fontWeight: 700 }}>
                                          לא ממופה
                                        </span>
                                      )}
                                      <span style={{ color: COLOR_DANGER, fontSize: 11,
                                                     marginInlineStart: 6 }}>
                                        {b.needs_alias
                                          ? 'שם המיקום לא מוכר — צריך מיפוי'
                                          : b.self_naming
                                          ? (b.clears_threshold
                                              ? 'ייקלט בסריקה הבאה'
                                              : 'מתחת לסף האימוץ')
                                          : 'צריך זיהוי ידני'}
                                      </span>
                                      {b.self_naming && !b.clears_threshold && (
                                        <button
                                          onClick={() => adoptNow(c.chain_key, b.theater_id)}
                                          disabled={adopting === `${c.chain_key}-${b.theater_id}`}
                                          style={{
                                            marginInlineStart: 8, padding: '2px 10px',
                                            borderRadius: 6, border: '1px solid var(--brd)',
                                            background: 'var(--surf)', color: 'var(--txt)',
                                            fontSize: 11, cursor: 'pointer',
                                            fontFamily: 'Heebo, sans-serif',
                                          }}
                                        >{adopting === `${c.chain_key}-${b.theater_id}`
                                            ? 'קולט...' : 'אמץ בכל זאת'}</button>
                                      )}
                                      {!b.self_naming && !b.needs_alias && (
                                        <button
                                          onClick={() => runProbe(b.theater_id)}
                                          disabled={probe[b.theater_id] === 'loading'}
                                          style={{
                                            marginInlineStart: 8, padding: '2px 10px',
                                            borderRadius: 6, border: '1px solid var(--brd)',
                                            background: 'var(--surf)', color: 'var(--txt)',
                                            fontSize: 11, cursor: 'pointer',
                                            fontFamily: 'Heebo, sans-serif',
                                          }}
                                        >{probe[b.theater_id] === 'loading'
                                            ? 'מחפש...' : 'זהה את הסניף'}</button>
                                      )}
                                      {adoptMsg[`${c.chain_key}-${b.theater_id}`] && (
                                        <div style={{ fontSize: 11, marginTop: 4,
                                          color: adoptMsg[`${c.chain_key}-${b.theater_id}`].error
                                            ? COLOR_DANGER : COLOR_OK }}>
                                          {adoptMsg[`${c.chain_key}-${b.theater_id}`].error
                                            || `נקלט: ${adoptMsg[`${c.chain_key}-${b.theater_id}`].name_he}`}
                                        </div>
                                      )}
                                      {probe[b.theater_id] && probe[b.theater_id] !== 'loading' && (
                                        <div style={{ fontSize: 11, marginTop: 4,
                                          color: probe[b.theater_id].error ? COLOR_DANGER : 'var(--txt)' }}>
                                          {probe[b.theater_id].error || (
                                            <>
                                              <b>{probe[b.theater_id].branch}</b>
                                              {' · siteCode '}{probe[b.theater_id].site_code}
                                              <pre style={{
                                                margin: '4px 0 0', padding: 6, borderRadius: 6,
                                                background: 'var(--surf-alt)', fontSize: 10,
                                                direction: 'ltr', textAlign: 'left',
                                                overflowX: 'auto', whiteSpace: 'pre',
                                              }}>{`THEATER_ID_TO_CINEMA:    ${probe[b.theater_id].add_to_maps.THEATER_ID_TO_CINEMA}
THEATER_ID_TO_SITE_CODE: ${probe[b.theater_id].add_to_maps.THEATER_ID_TO_SITE_CODE}`}</pre>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </span>
                                  )}
                                  {b.mapped_name && !b.has_cinema_row && (
                                    <span style={{ color: COLOR_DANGER, fontSize: 11,
                                                   marginInlineStart: 6 }}>
                                      (אין שורת סניף במסד!)
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '7px 12px', fontSize: 12,
                                             fontVariantNumeric: 'tabular-nums' }}>{b.screenings}</td>
                                <td style={{ padding: '7px 12px', fontSize: 12,
                                             fontVariantNumeric: 'tabular-nums' }}>{b.days}</td>
                                <td style={{ padding: '7px 12px', fontSize: 11,
                                             color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                                  {b.first_date} – {b.last_date}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
                {scan.threshold && (
                  <div style={{ fontSize: 11, color: 'var(--faint)' }}>
                    סף האימוץ: לפחות {scan.threshold.screenings} הקרנות על פני
                    {' '}{scan.threshold.days} ימים לפחות.
                  </div>
                )}
              </div>
            )}
          </div>

          {merges.screenings > 0 && (
            <div style={{ fontSize: 11, color: 'var(--muted)', padding: '8px 12px',
                          borderRadius: 8, background: 'var(--surf-alt)' }}>
              אוחדו {merges.screenings} הקרנות כפולות של סניפים שנרשמו יותר מפעם אחת
              {merges.last_at && ` (לאחרונה ${merges.last_at.slice(0, 10)})`}. כל סניף מזוהה
              עכשיו לפי המזהה של הרשת, כך שתיקון שם לא יפתח אותו מחדש.
            </div>
          )}

          {pending.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--txt)', marginBottom: 4 }}>
                נקלטו אוטומטית — לאישור
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
                השם הגיע מהרשת עצמה. העיר <b>מוסקת</b>, והיא מפתח קיבוץ בכל האנליטיקה —
                לכן שווה לוודא אותה. ההקרנות כבר נאספות בינתיים.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pending.map((row) => (
                  <div key={row.cinema_id} style={{
                    border: `1px solid ${row.city_unknown ? 'var(--warn)' : 'var(--brd)'}`,
                    borderRadius: 10, padding: 12, background: 'var(--surf-alt)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8,
                                  flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{row.name_he || row.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--faint)' }}>{row.chain_he || row.chain}</span>
                      {row.theater_id != null && (
                        <span style={{ fontSize: 11, color: 'var(--faint)' }}>מזהה {row.theater_id}</span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--muted)', marginInlineStart: 'auto' }}>
                        {row.screenings} הקרנות · נקלט ב-{(row.adopted_at || '').slice(0, 10)}
                      </span>
                    </div>
                    {row.city_unknown && (
                      <div style={{ fontSize: 11, color: 'var(--warn)', marginBottom: 8 }}>
                        לא הצלחנו להסיק את העיר משם הסניף — כרגע רשום "ישראל".
                      </div>
                    )}
                    <div style={{ display: 'grid', gap: 8,
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                      <label style={{ fontSize: 10, color: 'var(--faint)' }}>
                        שם הסניף
                        <input style={input} value={field(row.cinema_id, 'name_he', row.name_he || '')}
                               onChange={(e) => setField(row.cinema_id, 'name_he', e.target.value)} />
                      </label>
                      <label style={{ fontSize: 10, color: 'var(--faint)' }}>
                        עיר (עברית)
                        <input style={input} value={field(row.cinema_id, 'city_he', row.city_he || '')}
                               onChange={(e) => setField(row.cinema_id, 'city_he', e.target.value)} />
                      </label>
                      <label style={{ fontSize: 10, color: 'var(--faint)' }}>
                        עיר (אנגלית)
                        <input style={input} value={field(row.cinema_id, 'city', row.city || '')}
                               onChange={(e) => setField(row.cinema_id, 'city', e.target.value)} />
                      </label>
                    </div>
                    <button
                      onClick={() => save(row)}
                      disabled={saving === row.cinema_id}
                      style={{
                        marginTop: 10, padding: '6px 16px', borderRadius: 7, border: 'none',
                        background: ACCENT, color: '#fff', fontWeight: 700, fontSize: 12,
                        cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
                      }}
                    >{saving === row.cinema_id ? 'שומר...' : 'אישור הסניף'}</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {unmapped.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--txt)', marginBottom: 4 }}>
                מזהי סניף לא ממופים
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
                הרשת משדרת את המזהים האלה ואנחנו זורקים את ההקרנות שלהם. הוט סינמה לא
                יכולה להיקלט לבד — ה-login שלה דורש siteCode שלא נמצא בפיד. הבדיקה למטה
                מוצאת אותו, ואת שתי השורות מוסיפים למפות בקוד.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {unmapped.map((row) => {
                  const res = probe[row.theater_id];
                  return (
                    <div key={`${row.chain}-${row.theater_id}`} style={{
                      border: '1px solid var(--brd)', borderRadius: 10, padding: 12,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 700 }}>{row.chain}</span>
                        <span style={{ fontSize: 12, color: 'var(--muted)',
                                       fontFamily: 'monospace' }}>TheaterId {row.theater_id}</span>
                        <span style={{ fontSize: 12, color: COLOR_DANGER, fontWeight: 700 }}>
                          {row.screenings} הקרנות נזרקו
                        </span>
                        {row.probe_available && (
                          <button
                            onClick={() => runProbe(row.theater_id)}
                            disabled={res === 'loading'}
                            style={{
                              marginInlineStart: 'auto', padding: '5px 14px', borderRadius: 7,
                              border: '1px solid var(--brd)', background: 'var(--surf-alt)',
                              color: 'var(--txt)', fontSize: 12, cursor: 'pointer',
                              fontFamily: 'Heebo, sans-serif',
                            }}
                          >{res === 'loading' ? 'מחפש...' : 'זהה את הסניף'}</button>
                        )}
                      </div>
                      {res && res !== 'loading' && (
                        <div style={{
                          marginTop: 10, padding: 10, borderRadius: 8,
                          background: 'var(--surf-alt)', fontSize: 12,
                          color: res.error ? COLOR_DANGER : 'var(--txt)',
                        }}>
                          {res.error ? res.error : (
                            <>
                              <div><b>{res.branch}</b> · siteCode {res.site_code} ·
                                {' '}נבדקו {res.candidates_tried} מועמדים</div>
                              <div style={{ color: 'var(--muted)', marginTop: 2 }}>
                                אומת מול "{res.movie}" באולם {res.hall} ב-{(res.showtime || '').replace('T', ' ')}
                              </div>
                              <pre style={{
                                margin: '8px 0 0', padding: 8, borderRadius: 6,
                                background: 'var(--surf)', fontSize: 11, direction: 'ltr',
                                textAlign: 'left', overflowX: 'auto',
                              }}>{`hot_cinema.py THEATER_ID_TO_CINEMA:    ${res.add_to_maps.THEATER_ID_TO_CINEMA}
hot_cinema.py THEATER_ID_TO_SITE_CODE: ${res.add_to_maps.THEATER_ID_TO_SITE_CODE}`}</pre>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function ImpossibleScreeningsPanel({ refreshNonce }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchImpossibleScreenings().then(setData).catch(() => setData(null));
  }, [refreshNonce]);

  if (!data || !data.total) return null;

  const th = {
    padding: '8px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700,
    color: 'var(--faint)', borderBottom: '1px solid var(--brd)',
    background: 'var(--surf-alt)', whiteSpace: 'nowrap',
  };
  const td = { padding: '9px 12px', fontSize: 12, color: 'var(--txt)' };

  return (
    <div style={{ background: 'var(--surf)', borderRadius: 16,
                  boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', padding: '14px 20px', background: 'none', border: 'none',
          borderBottom: open ? '1px solid var(--brd)' : 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10, textAlign: 'right',
          fontFamily: 'Heebo, sans-serif',
        }}
      >
        <span style={{ fontSize: 18 }}>📅</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--txt)' }}>
            הקרנות לפני תאריך היציאה של הסרט ({data.total})
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            בדיקה מול TMDB, ללא תלות ביומן ההעברות — כך מתגלה נזק שלא תועד.
            {data.movies_without_release_date > 0 &&
              ` ${data.movies_without_release_date} סרטים ללא תאריך יציאה אינם ניתנים לבדיקה כך.`}
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--faint)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 660 }}>
            <thead>
              <tr>
                <th style={th}>סרט</th>
                <th style={th}>מועד ההקרנה</th>
                <th style={th}>תאריך יציאה</th>
                <th style={th}>ימים לפני</th>
                <th style={th}>סניף</th>
                <th style={th}>כרטיסים</th>
              </tr>
            </thead>
            <tbody>
              {data.screenings.map((sc) => (
                <tr key={sc.screening_id} style={{ borderBottom: '1px solid var(--faint)' }}>
                  <td style={td}>{sc.title || `#${sc.movie_id}`}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{sc.showtime}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                    {sc.released}
                  </td>
                  <td style={{ ...td, color: COLOR_DANGER, fontWeight: 700 }}>
                    {sc.days_before}
                  </td>
                  <td style={{ ...td, fontSize: 11 }}>{sc.cinema || '—'}</td>
                  <td style={td}>{sc.tickets_sold}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ScreeningConflictsPanel({ refreshNonce }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchScreeningConflicts(50).then(setData).catch(() => setData(null));
  }, [refreshNonce]);

  if (!data || !data.total) return null;

  const cardStyle = {
    background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)',
    overflow: 'hidden',
  };
  const th = {
    padding: '8px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700,
    color: 'var(--faint)', borderBottom: '1px solid var(--brd)',
    background: 'var(--surf-alt)', whiteSpace: 'nowrap',
  };
  const td = { padding: '9px 12px', fontSize: 12, color: 'var(--txt)' };

  return (
    <div style={cardStyle}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', padding: '14px 20px', background: 'none', border: 'none',
          borderBottom: open ? '1px solid var(--brd)' : 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10, textAlign: 'right',
          fontFamily: 'Heebo, sans-serif',
        }}
      >
        <span style={{ fontSize: 18 }}>🔀</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--txt)' }}>
            הקרנות כפולות שאוחדו ({data.total})
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            שני סרטים באותו אולם באותה שעה — הרשת שינתה שם או החליפה סרט.
            הכרטיסים נספרו פעמיים עד האיחוד.
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--faint)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
            <thead>
              <tr>
                <th style={th}>מתי</th>
                <th style={th}>סניף</th>
                <th style={th}>אולם</th>
                <th style={th}>מועד ההקרנה</th>
                <th style={th}>נשמר</th>
                <th style={th}>הוסר</th>
                <th style={th}>נקודות שהועברו</th>
                <th style={th}>סיבה</th>
              </tr>
            </thead>
            <tbody>
              {data.conflicts.map((c) => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--faint)' }}>
                  <td style={{ ...td, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    {formatIsraelDateTime(c.created_at)}
                  </td>
                  <td style={td}>{c.cinema || '—'}</td>
                  <td style={td}>{c.hall || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.showtime || '—'}</td>
                  <td style={{ ...td, color: COLOR_OK }}>
                    {c.kept_title || '—'}
                    {c.tickets_kept != null && (
                      <span style={{ color: 'var(--faint)', fontSize: 10 }}> ({c.tickets_kept})</span>
                    )}
                  </td>
                  <td style={{ ...td, color: COLOR_DANGER }}>
                    {c.removed_title || '—'}
                    {c.tickets_removed != null && (
                      <span style={{ color: 'var(--faint)', fontSize: 10 }}> ({c.tickets_removed})</span>
                    )}
                  </td>
                  <td style={td}>{c.snapshots_moved || 0}</td>
                  <td style={{ ...td, color: 'var(--muted)', fontSize: 11 }}>
                    {c.reason === 'booking_url'
                      ? 'שינוי שם (אותו מזהה הזמנה)'
                      : c.reason === 'delisted'
                        ? 'הוסרה מלוח הזמנים של הרשת'
                        : c.reason === 'duplicate_branch'
                          ? 'סניף שנפתח פעמיים — אוחד'
                          : c.reason === 'moved'
                            ? 'הועברה לשעה או לאולם אחר — אוחד'
                            : 'התנגשות אולם'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ScrapePage({ refreshNonce }) {
  const { hasPermission } = useAuth();
  const [logs, setLogs] = useState([]);
  const [closingScreenings, setClosingScreenings] = useState([]);
  const handleTodayData = useCallback(d => setClosingScreenings(d?.closing_screenings || []), []);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [message, setMessage] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const mob = width < 768;

  const loadLogs = useCallback(() => {
    fetchScrapeLogs()
      .then(setLogs)
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, []);

  // Find the running log (if any)
  const runningLog = logs.find((l) => l.status === 'running');

  useEffect(() => {
    loadLogs();
    const interval = setInterval(loadLogs, runningLog ? 3000 : 10000);
    return () => clearInterval(interval);
  }, [loadLogs, runningLog]);

  useEffect(() => {
    loadLogs();
  }, [refreshNonce, loadLogs]);

  const handleTrigger = (chain) => {
    setScraping(true);
    setMessage(null);
    triggerScrape(chain)
      .then((res) => {
        setMessage(res.message || 'סריקה הופעלה');
        setTimeout(loadLogs, 2000);
      })
      .catch(() => setMessage('שגיאה בהפעלת הסריקה'))
      .finally(() => setScraping(false));
  };

  const handleTicketScan = (chain) => {
    setScraping(true);
    setMessage(null);
    triggerTicketScan(chain)
      .then((res) => {
        setMessage(res.message || 'סריקת כיסאות הופעלה');
        setTimeout(loadLogs, 2000);
      })
      .catch(() => setMessage('שגיאה בהפעלת סריקת הכיסאות'))
      .finally(() => setScraping(false));
  };

  const statusColor = (status) => {
    if (status === 'success') return 'var(--ok)';
    if (status === 'error' || status === 'failed') return 'var(--danger)';
    if (status === 'running') return 'var(--danger)';
    return 'var(--warn)';
  };

  const statusLabel = (status) => {
    if (status === 'running') return 'רץ...';
    return status;
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: TEXT_TERTIARY }}>טוען נתונים...</div>;
  }

  const runningChain = runningLog ? (runningLog.chain || null) : (scraping ? 'all' : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {hasPermission('scrape.approve_blocked') && <PendingBlockedApprovals />}

      {hasPermission('scrape.schedule_panel') && <NewBranchesPanel refreshNonce={refreshNonce} />}
      {hasPermission('scrape.schedule_panel') && <ScreeningRepointsPanel refreshNonce={refreshNonce} />}
      {hasPermission('scrape.schedule_panel') && <ScreeningGapPanel refreshNonce={refreshNonce} />}
      {hasPermission('scrape.schedule_panel') && <ImpossibleScreeningsPanel refreshNonce={refreshNonce} />}
      {hasPermission('scrape.schedule_panel') && <ScreeningConflictsPanel refreshNonce={refreshNonce} />}

      {hasPermission('scrape.lev_seatplan') && <LevMissingSeatplans />}

      {/* Chain trigger grid */}
      {(hasPermission('scrape.trigger_full') || hasPermission('scrape.trigger_tickets')) && <div style={{ background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)', padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>הפעלת סריקה לפי רשת</div>
          <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 2 }}>לחץ על &#39;סרוק&#39; להפעלת סריקה מלאה, &#39;כיסאות&#39; לסריקת כרטיסים בלבד</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: mob ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
          {CHAINS.map(ch => (
            <ChainCard key={ch.key} chain={ch} running={runningChain} onScrape={handleTrigger} onTickets={handleTicketScan} />
          ))}
        </div>
        <div style={{ borderTop: '1px solid var(--brd)', paddingTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            disabled={!!runningChain}
            onClick={() => handleTrigger()}
            style={{
              padding: '10px 28px', borderRadius: 9, border: 'none',
              background: runningChain ? 'var(--faint)' : 'var(--danger)', color: '#fff',
              fontWeight: 700, fontSize: 14, cursor: runningChain ? 'not-allowed' : 'pointer',
              fontFamily: 'Heebo,sans-serif', opacity: runningChain ? 0.5 : 1,
            }}
          >{runningChain === 'all' ? 'רץ...' : '⚡ סרוק הכל במקביל'}</button>
          {runningChain && (
            <button
              onClick={async () => {
                try { const r = await cancelAllScrapes(); setMessage(r.message || 'כל הסריקות בוטלו.'); setScraping(false); loadLogs(); }
                catch (e) { setMessage('שגיאה בביטול הסריקות.'); }
              }}
              style={{
                padding: '10px 24px', borderRadius: 9, border: 'none',
                background: 'var(--danger)', color: '#fff',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
                fontFamily: 'Heebo,sans-serif',
              }}
            >עצור הכל</button>
          )}
        </div>
      </div>}

      {message && (
        <div style={{ padding: '12px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600, background: 'var(--ok-bg)', color: 'var(--ok)', border: '1px solid rgba(22,163,74,0.3)' }}>
          {message}
        </div>
      )}

      {/* Live schedule for today */}
      {hasPermission('scrape.schedule_panel') && (
        <ScheduleTodayPanel
          refreshNonce={refreshNonce}
          onData={handleTodayData}
        />
      )}

      {/* Missed final scan panel */}
      {hasPermission('scrape.schedule_panel') && <MissedFinalScanPanel closingScreenings={closingScreenings} />}

      {/* Live Progress Indicators - show all running logs */}
      {logs.filter(l => l.status === 'running' && l.progress).map((log) => (
        <ProgressCard key={log.id} progress={log.progress} chainName={log.chain_name} />
      ))}

      {/* Logs Table */}
      {hasPermission('scrape.logs') && <div style={{ background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--brd)' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--txt)' }}>לוגים אחרונים</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surf-alt)' }}>
                {['תאריך', 'רשת', 'סטטוס', 'סרטים', 'הקרנות', 'משך', 'שגיאה'].map((h, i) => (
                  <th key={i} style={{ padding: '10px 16px', textAlign: i > 1 ? 'center' : 'right', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)', borderBottom: '1px solid var(--brd)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--faint)', fontSize: 13 }}>
                    אין לוגים עדיין. הפעל סריקה ראשונה!
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: `1px solid var(--brd)` }}>
                    <td style={{ padding: '11px 16px', fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {formatIsraelDateTime(log.created_at)}
                    </td>
                    <td style={{ padding: '11px 16px', fontWeight: 600, fontSize: 13, color: 'var(--txt)' }}>{log.chain_name}</td>
                    <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: `${statusColor(log.status)}18`,
                        color: statusColor(log.status),
                      }}>
                        {log.status === 'running' && (
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--danger)', display: 'inline-block', animation: 'pulse-dot 1.5s ease-in-out infinite' }} />
                        )}
                        {statusLabel(log.status)}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>{log.movies_found ?? '—'}</td>
                    <td style={{ padding: '11px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>{log.screenings_found ?? '—'}</td>
                    <td style={{ padding: '11px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>{log.duration_seconds != null ? `${log.duration_seconds.toFixed(1)}ש` : '—'}</td>
                    <td style={{ padding: '11px 16px', color: 'var(--danger)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {log.error_message || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>}

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.4); }
        }
        @keyframes progress-stripe {
          0% { background-position: 0 0; }
          100% { background-position: 40px 0; }
        }
      `}</style>
    </div>
  );
}


// ── Screenshot parsing & labels ──────────────────────────────────────────

const STEP_LABELS = {
  page: 'עמוד ראשי',
  branch: 'עמוד סניף',
  dropdown: 'תפריט סניפים',
  booking: 'דף הזמנה',
  step1_booking: 'דף הזמנה',
  step2_plus_click: 'אחרי +',
  step3_continue: 'אחרי המשך',
  step4_seat_map: 'מפת כיסאות',
  step4_seat_map_shortcut: 'מפת כיסאות (קיצור)',
  step5_annotated: 'כיסאות מסומנים',
  step5_mismatched: 'דף כרטיסים לא תואם (mismatch)',
  seats: 'מפת כיסאות',
  tickets: 'כרטיסים',
  discover_empty: 'רשימת סרטים ריקה',
  no_branch_dropdown: 'תפריט סניפים לא נמצא',
  empty_branch_dropdown: 'תפריט סניפים ריק',
  dl_not_found: 'תפריט סניפים לא נטען (dl)',
  captcha_blocked: 'דף כרטיסים חסום (reCAPTCHA)',
};

const CHAIN_LABELS = {
  hot: 'הוט סינמה',
  mvl: 'מובילנד',
  cc: 'סינמה סיטי',
  cinema_city: 'סינמה סיטי',
  lev: 'לב',
  planet: 'פלאנט',
  rav_hen: 'רב-חן',
  cinematheque_tlv:      'סינמטק תל אביב',
  cinematheque_jlm:      'סינמטק ירושלים',
  cinematheque_herzliya: 'סינמטק הרצליה',
  cinematheque_holon:    'סינמטק חולון',
  cinematheque_haifa:    'סינמטק חיפה',
  cinematheque_rosh_pina: 'סינמטק ראש פינה',
  cinematheque_sderot:   'סינמטק שדרות',
};

const CHAIN_COLORS = {
  hot: COLOR_DANGER,
  mvl: '#e11d48',
  cc: '#7c3aed',
  cinema_city: '#7c3aed',
  lev: '#92400e',
  planet: '#075985',
  rav_hen: '#0f766e',
  cinematheque_tlv:       '#4f46e5',
  cinematheque_jlm:       '#7c3aed',
  cinematheque_herzliya:  '#059669',
  cinematheque_holon:     '#db2777',
  cinematheque_haifa:     '#0e7490',
  cinematheque_rosh_pina: '#ea580c',
  cinematheque_sderot:    '#854d0e',
};

// All known step keywords (both single and compound)
const STEP_KEYWORDS_SINGLE = ['page', 'branch', 'dropdown', 'booking', 'seats', 'tickets'];
const STEP_KEYWORDS_COMPOUND = [
  'step1_booking', 'step2_plus_click', 'step3_continue',
  'step4_seat_map', 'step4_seat_map_shortcut', 'step5_annotated', 'step5_mismatched',
  'discover_empty', 'no_branch_dropdown', 'empty_branch_dropdown', 'dl_not_found',
  'captcha_blocked',
];

function parseScreenshotFilename(filename) {
  // Format: {chain}_{branch}_{movie}_{time}_{step}_{timestamp}.png
  // Timestamp is always 6 digits (HHMMSS) at the end before .png
  // Step is the second-to-last segment (or compound like step4_seat_map)
  const name = filename.replace('.png', '');
  const parts = name.split('_');

  let chain = 'unknown';
  let branch = '';
  let movie = '';
  let time = '';
  let step = '';

  if (parts[0] === 'hot' || parts[0] === 'mvl' || parts[0] === 'cc' || parts[0] === 'cinema_city') {
    chain = parts[0];
  } else {
    // Legacy format - just return basic info
    return { chain: 'unknown', branch: '', movie: '', time: '', step: parts[0] || '' };
  }

  // Last part is always timestamp (6 digits)
  // Work backwards to find the step
  const lastPart = parts[parts.length - 1];
  let timestampIdx = parts.length - 1;
  if (/^\d{6}$/.test(lastPart)) {
    timestampIdx = parts.length - 1;
  }

  // Find the step by scanning from the end (before timestamp)
  let stepStartIdx = -1;
  let stepEndIdx = timestampIdx;

  // Try compound step names first (scan backwards for step patterns)
  for (let i = timestampIdx - 1; i >= 1; i--) {
    // Try 3-part compound: step4_seat_map_shortcut
    if (i >= 3) {
      const compound3 = parts[i - 2] + '_' + parts[i - 1] + '_' + parts[i];
      if (STEP_KEYWORDS_COMPOUND.includes(compound3)) {
        step = compound3;
        stepStartIdx = i - 2;
        break;
      }
    }
    // Try 2-part compound: step1_booking, step4_seat_map, etc.
    if (i >= 2) {
      const compound2 = parts[i - 1] + '_' + parts[i];
      if (STEP_KEYWORDS_COMPOUND.includes(compound2)) {
        step = compound2;
        stepStartIdx = i - 1;
        break;
      }
    }
    // Try single keyword
    if (STEP_KEYWORDS_SINGLE.includes(parts[i])) {
      step = parts[i];
      stepStartIdx = i;
      break;
    }
  }

  // Everything between chain (idx 0) and step is context
  // Context order: branch, movie parts..., time (4 digits)
  if (stepStartIdx > 1) {
    const contextParts = parts.slice(1, stepStartIdx);

    // Last context part might be a time (4 digits like 2030)
    if (contextParts.length > 0) {
      const lastCtx = contextParts[contextParts.length - 1];
      if (/^\d{4}$/.test(lastCtx)) {
        time = lastCtx.substring(0, 2) + ':' + lastCtx.substring(2);
        contextParts.pop();
      }
    }

    // First context part is branch, rest is movie
    if (contextParts.length >= 1) {
      branch = contextParts[0];
    }
    if (contextParts.length >= 2) {
      movie = contextParts.slice(1).join(' ');
    }
  } else if (stepStartIdx === 1) {
    // No context between chain and step
  } else {
    // Couldn't find step - treat everything except chain and timestamp as context
    const contextParts = parts.slice(1, timestampIdx);
    if (contextParts.length >= 1) {
      branch = contextParts[0];
    }
    if (contextParts.length >= 2) {
      movie = contextParts.slice(1).join(' ');
    }
  }

  return { chain, branch, movie, time, step };
}

function getStepLabel(step) {
  return STEP_LABELS[step] || step;
}

// ── Lev missing seatplans panel ──────────────────────────────────

function LevMissingSeatplans() {
  const [missing, setMissing] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchLevMissingSeatplans()
      .then(d => setMissing(d?.missing || []))
      .catch(() => setMissing([]));
  }, []);

  if (missing.length === 0) return null;

  return (
    <div style={{
      background: 'rgba(146,64,14,0.12)',
      border: '1px solid #92400e',
      borderRadius: 12,
      padding: '14px 18px',
      marginBottom: 20,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: COLOR_WARN }}>
          ⚠ אולמות לב חסרים ({missing.length}) — נדרש איסוף ידני
        </div>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ background: 'none', border: 'none', color: COLOR_WARN, cursor: 'pointer', fontSize: 13 }}
        >
          {open ? 'הסתר הוראות ▲' : 'הצג הוראות ▼'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8, marginBottom: open ? 12 : 0 }}>
        {missing.map(m => (
          <div key={`${m.venue_id}:${m.seatplan_id}`} style={{
            background: 'var(--surf-alt)',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 13,
            border: '1px solid #78350f',
          }}>
            <div style={{ color: COLOR_WARN, fontWeight: 600, marginBottom: 4 }}>
              venue_id={m.venue_id} / seatplan_id={m.seatplan_id}
            </div>
            <div style={{ color: TEXT_SECONDARY }}>
              {m.affected_count} הקרנות פעילות מושפעות
            </div>
            <a
              href={m.example_booking_url}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#7dd3fc', fontSize: 12, wordBreak: 'break-all' }}
            >
              פתח דוגמה בדפדפן
            </a>
          </div>
        ))}
      </div>

      {open && (
        <div style={{
          background: 'var(--surf-alt)',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 12,
          color: TEXT_SECONDARY,
          lineHeight: 1.8,
          direction: 'rtl',
        }}>
          <div style={{ fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 6 }}>הוראות איסוף לכל אולם:</div>
          <ol style={{ margin: 0, paddingRight: 18 }}>
            <li>לחץ "פתח דוגמה בדפדפן" → פתח DevTools → לשונית Network</li>
            <li>רענן את הדף ומצא בקשת POST ל-<code>/api/seats/seatplanV2</code> עם status 200</li>
            <li>לחץ עליה → Response → בחר הכל → העתק → שמור לקובץ <code>hall.json</code></li>
            <li>הרץ: <code>python backend/scripts/lev_add_seatplan.py &lt;venue_id&gt; &lt;seatplan_id&gt; hall.json</code></li>
            <li>הפעל מחדש את הבאקנד — האולם יופיע ברשימה אחרי ריסטארט</li>
          </ol>
        </div>
      )}
    </div>
  );
}


// ── Today schedule panel ─────────────────────────────────────────

const REASON_STYLE = {
  'חסר כתובת הזמנה':        { bg: 'var(--danger-bg)', color: 'var(--danger)' },
  'כתובת הזמנה פגת תוקף':   { bg: 'var(--warn-bg)',   color: 'var(--warn)'   },
  'חסימת CAPTCHA':           { bg: 'var(--danger-bg)', color: 'var(--danger)' },
  'אי-התאמה בנתוני האימות': { bg: 'var(--warn-bg)',   color: 'var(--warn)'   },
  'שרת לא פעל בחלון הסריקה':{ bg: 'var(--surf-alt)',  color: 'var(--faint)'  },
  'חלון חלף — סיבה לא ידועה':{ bg: 'var(--surf-alt)', color: 'var(--faint)'  },
  'הקרנה מבוטלת':             { bg: 'var(--surf-alt)', color: 'var(--faint)'  },
};

function ReasonBadge({ reason }) {
  const s = REASON_STYLE[reason] || { bg: 'var(--surf-alt)', color: 'var(--faint)' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
      fontSize: 11, fontWeight: 700, background: s.bg, color: s.color,
    }}>
      {reason || '—'}
    </span>
  );
}

function MissedFinalScanPanel({ closingScreenings }) {
  const [logModal, setLogModal] = useState(null);
  const [logLines, setLogLines] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const missed = (closingScreenings || []).filter(s => s.status === 'missed_closed' || s.status === 'cancelled');

  const openLogs = (row) => {
    setLogModal(row);
    setLogLines([]);
    setLogLoading(true);
    fetchDebugLogs('all', { search: String(row.screening_id), limit: 300 })
      .then(d => setLogLines(d.lines || []))
      .catch(() => setLogLines([]))
      .finally(() => setLogLoading(false));
  };

  if (!missed.length) return null;

  const cardStyle = {
    background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)',
    overflow: 'hidden',
  };
  const thS = {
    padding: '8px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700,
    letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)',
    borderBottom: '1px solid var(--brd)', background: 'var(--surf-alt)',
  };
  const tdS = { padding: '10px 12px', fontSize: 13, color: 'var(--txt)', verticalAlign: 'middle' };

  return (
    <div style={cardStyle}>
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid var(--brd)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 18 }}>⚠️</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--danger)' }}>
            הקרנות שלא נסגרו ({missed.length})
          </div>
          <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>
            הקרנות שחלון הסריקה הסופית שלהן עבר ללא רישום snapshot
          </div>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['סרט', 'רשת', 'אולם', 'שעה', 'סיבה', "snap'", 'לוגים'].map((h, i) => (
                <th key={i} style={thS}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {missed.map(row => (
              <tr key={row.screening_id} style={{ borderBottom: '1px solid var(--brd)' }}>
                <td style={{ ...tdS, fontWeight: 600 }}>{row.movie}</td>
                <td style={tdS}>{row.cinema}</td>
                <td style={tdS}>{row.hall || '—'}</td>
                <td style={{ ...tdS, fontVariantNumeric: 'tabular-nums' }}>
                  {formatIsraelTime(row.showtime)}
                </td>
                <td style={tdS}><ReasonBadge reason={row.failure_reason} /></td>
                <td style={{ ...tdS, textAlign: 'center', color: 'var(--faint)' }}>
                  {row.snapshot_count ?? '—'}
                </td>
                <td style={tdS}>
                  <button
                    onClick={() => openLogs(row)}
                    style={{
                      padding: '4px 10px', borderRadius: 7, border: '1px solid var(--brd)',
                      background: 'var(--surf-alt)', color: 'var(--txt)',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'Heebo,sans-serif',
                    }}
                  >לוגים</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {logModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }} onClick={e => e.target === e.currentTarget && setLogModal(null)}>
          <div style={{
            background: 'var(--surf)', borderRadius: 16, width: '100%', maxWidth: 760,
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: 'var(--shadow)',
          }}>
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid var(--brd)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {logModal.movie}
                  <span style={{ fontWeight: 400, color: 'var(--faint)', marginRight: 8, fontSize: 12 }}>
                    #{logModal.screening_id}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>
                  {logModal.cinema} · {formatIsraelTime(logModal.showtime)}
                </div>
              </div>
              <button
                onClick={() => setLogModal(null)}
                style={{
                  padding: '6px 14px', borderRadius: 8, border: '1px solid var(--brd)',
                  background: 'var(--surf-alt)', cursor: 'pointer',
                  fontSize: 13, fontFamily: 'Heebo,sans-serif', color: 'var(--txt)',
                }}
              >סגור</button>
            </div>
            <div style={{ overflowY: 'auto', padding: 16, flex: 1 }}>
              {logLoading ? (
                <div style={{ textAlign: 'center', color: 'var(--faint)', padding: 32 }}>טוען לוגים...</div>
              ) : logLines.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--faint)', padding: 32 }}>
                  אין לוגים עבור הקרנה #{logModal.screening_id}
                  <div style={{ fontSize: 11, marginTop: 8 }}>
                    לוגים עם screening_id מתועדים רק החל מגרסה זו
                  </div>
                </div>
              ) : (
                <pre style={{
                  fontSize: 11, lineHeight: 1.6, color: 'var(--txt)',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0,
                }}>
                  {logLines.map(l => l.line || l.text || JSON.stringify(l)).join('\n')}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ScheduleTodayPanel({ refreshNonce, onData }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  const loadSchedule = useCallback(() => {
    fetchScheduleToday()
      .then(d => { setData(d); onData && onData(d); })
      .catch(() => { setData(null); onData && onData(null); })
      .finally(() => setLoading(false));
  }, [onData]);

  useEffect(() => {
    loadSchedule();
    const timer = setInterval(loadSchedule, 30000);
    return () => clearInterval(timer);
  }, [loadSchedule, refreshNonce]);

  const jobs = data?.jobs || [];
  const closing = data?.closing_screenings || [];
  const health = data?.ticket_health || {};
  const unscreened = health?.unscreened_active_today || {};
  const stale = health?.stale_scrapes_6h_today || {};
  const staleReasons = health?.stale_reasons_by_chain || {};
  const noUrl = health?.no_booking_url_near_future || {};
  const mvlDiag = data?.movieland_unscanned_diagnostics || {};
  const mvlVerify = data?.movieland_verification_diagnostics || {};
  const ccDiag = data?.cinema_city_unscanned_diagnostics || {};
  const ccCaptcha = data?.cinema_city_captcha_diagnostics || {};

  return (
    <div style={{
      background: NEU_BG,
      borderRadius: 12,
      border: `1px solid ${BORDER_SUBTLE}`,
      marginBottom: 24,
      overflow: 'hidden',
      boxShadow: NEU_RAISED,
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '14px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>לו״ז סריקות להיום (מתעדכן אוטומטית)</div>
          <div style={{ fontSize: 12, color: TEXT_TERTIARY }}>
            {data?.generated_at
              ? `עודכן לאחרונה: ${formatIsraelTime(data.generated_at)} (${data?.timezone || 'Asia/Jerusalem'})`
              : 'טוען...'}
          </div>
        </div>
        <span style={{ color: TEXT_TERTIARY, fontSize: 18 }}>{expanded ? '\u25B2' : '\u25BC'}</span>
      </div>

      {expanded && (
        <div style={{ padding: '0 20px 16px' }}>
          {loading ? (
            <div style={{ color: TEXT_TERTIARY, fontSize: 14, textAlign: 'center', padding: 18 }}>טוען לו״ז...</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 14 }}>
                <div style={{ border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 10, padding: '10px 12px', background: 'var(--surf-alt)' }}>
                  <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginBottom: 4 }}>הקרנות פעילות היום בלי ספירת כיסאות</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: (unscreened.total || 0) > 0 ? COLOR_WARN : COLOR_OK }}>
                    {unscreened.total || 0}
                  </div>
                  <div style={{ fontSize: 12, color: TEXT_SECONDARY }}>
                    הוט: {unscreened.hot_cinema || 0} | מובילנד: {unscreened.movieland || 0} | סינמה סיטי: {unscreened.cinema_city || 0} | לב: {unscreened.lev || 0} | פלאנט: {unscreened.planet || 0} | רב-חן: {unscreened.rav_hen || 0} | סינמטק: {unscreened.cinematheque_tlv || 0} | ירושלים: {unscreened.cinematheque_jlm || 0} | הרצליה: {unscreened.cinematheque_herzliya || 0} | חולון: {unscreened.cinematheque_holon || 0} | חיפה: {unscreened.cinematheque_haifa || 0} | ראש פינה: {unscreened.cinematheque_rosh_pina || 0} | שדרות: {unscreened.cinematheque_sderot || 0}
                  </div>
                </div>
                <div style={{ border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 10, padding: '10px 12px', background: 'var(--surf-alt)' }}>
                  <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginBottom: 4 }}>
                    הקרנות שלא נסרקו לאחרונה ({health.stale_cutoff_hours || 6} שעות)
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: (stale.total || 0) > 0 ? COLOR_DANGER : COLOR_OK }}>
                    {stale.total || 0}
                  </div>
                  <div style={{ fontSize: 12, color: TEXT_SECONDARY }}>
                    הוט: {stale.hot_cinema || 0} | מובילנד: {stale.movieland || 0} | סינמה סיטי: {stale.cinema_city || 0} | לב: {stale.lev || 0} | פלאנט: {stale.planet || 0} | רב-חן: {stale.rav_hen || 0} | סינמטק: {stale.cinematheque_tlv || 0} | ירושלים: {stale.cinematheque_jlm || 0} | הרצליה: {stale.cinematheque_herzliya || 0} | חולון: {stale.cinematheque_holon || 0} | חיפה: {stale.cinematheque_haifa || 0} | ראש פינה: {stale.cinematheque_rosh_pina || 0} | שדרות: {stale.cinematheque_sderot || 0}
                  </div>
                  {Object.keys(staleReasons).length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 11, color: TEXT_TERTIARY, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {[['Hot Cinema','הוט'],['Movieland','מובילנד'],['Cinema City','סינמה סיטי'],['Lev','לב'],['Planet','פלאנט'],['Rav-Chen','רב-חן'],['Cinematheque TLV','סינמטק ת"א'],['Cinematheque JLM','סינמטק י"ם'],['Cinematheque Herzliya','סינמטק הרצליה'],['Cinematheque Holon','סינמטק חולון'],['Cinematheque Haifa','סינמטק חיפה'],['Cinematheque Rosh Pina','סינמטק ראש פינה'],['Cinematheque Sderot','סינמטק שדרות']].map(([key, label]) => {
                        const r = staleReasons[key];
                        if (!r) return null;
                        const total = Object.values(r).reduce((a, b) => a + b, 0);
                        if (!total) return null;
                        const REASON_LABELS = { captcha: "רקאפצ'ה", stale_url: 'URL פג', mismatch: 'אי-התאמה', no_url: 'אין URL', unknown: 'לא ידוע' };
                        const parts = Object.entries(r).filter(([,v]) => v > 0).map(([k, v]) => `${REASON_LABELS[k]}: ${v}`);
                        return <div key={key}>{label}: {parts.join(' | ')}</div>;
                      })}
                    </div>
                  )}
                </div>
                <div style={{ border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 10, padding: '10px 12px', background: 'var(--surf-alt)' }}>
                  <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginBottom: 4 }}>
                    הקרנות בלי booking_url (36 שעות קדימה)
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: (noUrl.total || 0) > 0 ? COLOR_WARN : COLOR_OK }}>
                    {noUrl.total || 0}
                  </div>
                  <div style={{ fontSize: 12, color: TEXT_SECONDARY }}>
                    הוט: {noUrl.hot_cinema || 0} | מובילנד: {noUrl.movieland || 0} | סינמה סיטי: {noUrl.cinema_city || 0} | לב: {noUrl.lev || 0} | פלאנט: {noUrl.planet || 0} | רב-חן: {noUrl.rav_hen || 0} | סינמטק: {noUrl.cinematheque_tlv || 0} | ירושלים: {noUrl.cinematheque_jlm || 0} | הרצליה: {noUrl.cinematheque_herzliya || 0} | חולון: {noUrl.cinematheque_holon || 0} | חיפה: {noUrl.cinematheque_haifa || 0} | ראש פינה: {noUrl.cinematheque_rosh_pina || 0} | שדרות: {noUrl.cinematheque_sderot || 0}
                  </div>
                </div>
              </div>
              {!!mvlDiag.total_unscanned && (
                <div style={{
                  border: `1px dashed ${BORDER_SUBTLE}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  marginBottom: 14,
                  background: 'var(--surf-alt)',
                }}>
                  <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginBottom: 4 }}>
                    דיאגנוסטיקה מובילנד (הקרנות לא נסרקות, חלון 36 שעות)
                  </div>
                  <div style={{ fontSize: 13, color: TEXT_SECONDARY }}>
                    סה״כ: {mvlDiag.total_unscanned} | בלי URL: {mvlDiag.no_booking_url} | עם URL: {mvlDiag.has_booking_url}
                    {' '}| עם URL שלא נסרקו מעולם: {mvlDiag.never_scraped_with_url} | עם URL סריקה ישנה: {mvlDiag.stale_scraped_with_url}
                    {' '}| בלי אולם: {mvlDiag.no_hall}
                  </div>
                </div>
              )}
              {!!mvlVerify.currently_mismatched && (
                <div style={{
                  border: `1px solid ${COLOR_DANGER}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  marginBottom: 14,
                  background: 'var(--surf-alt)',
                }}>
                  <div style={{ fontSize: 12, color: COLOR_DANGER, marginBottom: 4 }}>
                    מובילנד — אי-התאמה בדף הכרטיסים (movie/branch/time)
                  </div>
                  <div style={{ fontSize: 13, color: TEXT_SECONDARY }}>
                    לא תואמות כעת: {mvlVerify.currently_mismatched} | זוהו אי-התאמות אי-פעם בחלון: {mvlVerify.total_mismatched_ever}
                  </div>
                  <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 4 }}>
                    הדף שנטען לא תאם לסרט/סניף/שעה הצפויים. תמונות debug: mvl_step5_mismatched_*.
                    בלוגים יופיע `VERIFICATION MISMATCH` עם השוואת expected vs page.
                  </div>
                </div>
              )}
              {!!ccDiag.total_unscanned && (
                <div style={{
                  border: `1px dashed ${BORDER_SUBTLE}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  marginBottom: 14,
                  background: 'var(--surf-alt)',
                }}>
                  <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginBottom: 4 }}>
                    דיאגנוסטיקה סינמה סיטי (הקרנות לא נסרקות, חלון 36 שעות)
                  </div>
                  <div style={{ fontSize: 13, color: TEXT_SECONDARY }}>
                    סה״כ: {ccDiag.total_unscanned} | בלי URL: {ccDiag.no_booking_url} | עם URL: {ccDiag.has_booking_url}
                    {' '}| עם URL שלא נסרקו מעולם: {ccDiag.never_scraped_with_url} | עם URL סריקה ישנה: {ccDiag.stale_scraped_with_url}
                    {' '}| בלי אולם: {ccDiag.no_hall}
                  </div>
                </div>
              )}
              {!!ccCaptcha.currently_blocked && (
                <div style={{
                  border: `1px solid #f59e0b`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  marginBottom: 14,
                  background: 'var(--surf-alt)',
                }}>
                  <div style={{ fontSize: 12, color: COLOR_WARN, marginBottom: 4 }}>
                    סינמה סיטי — חסימה ע״י reCAPTCHA בדף הכרטיסים (PresGlobal)
                  </div>
                  <div style={{ fontSize: 13, color: TEXT_SECONDARY }}>
                    חסומות כעת: {ccCaptcha.currently_blocked} | בוצעה חסימה אי-פעם בחלון: {ccCaptcha.total_blocked_ever}
                  </div>
                  <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 4 }}>
                    ה-booking_url נשמר אבל לא ניתן לספור מושבים; הסבר: אימות בוט של Google.
                    תמונות debug: cc_captcha_blocked_*.
                  </div>
                </div>
              )}

              <div style={{ overflowX: 'auto', marginBottom: 14 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                      <th style={thStyle}>Job</th>
                      <th style={thStyle}>רשת/סקרייפר</th>
                      <th style={thStyle}>סוג</th>
                      <th style={thStyle}>תזמון</th>
                      <th style={thStyle}>ריצה הבאה</th>
                      <th style={thStyle}>ריצה אחרונה</th>
                      <th style={thStyle}>סטטוס</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((j) => {
                      const last = j.last_run;
                      const status = j.status_today;
                      const color = status === 'ok' ? COLOR_OK : status === 'failed' ? COLOR_DANGER : COLOR_WARN;
                      const label = status === 'ok' ? 'תקין' : status === 'failed' ? 'נכשלה' : 'לא רצה מעולם';
                      return (
                        <tr key={j.job_id} style={{ borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                          <td style={tdStyle}>
                            {j.label}
                            <div style={{ fontSize: 11, color: TEXT_TERTIARY }}>{j.job_id}</div>
                          </td>
                          <td style={tdStyle}>{j.chain}</td>
                          <td style={tdStyle}>{j.schedule_type}</td>
                          <td style={tdStyle}>{j.planned_time || '—'}</td>
                          <td style={tdStyle}>
                            {j.paused ? <span style={{ color: COLOR_DANGER }}>מושהה</span> : (j.next_run || '—')}
                          </td>
                          <td style={tdStyle}>
                            {last ? (
                              <span title={last.error || last.summary || ''}>
                                {last.date} {last.at}
                                {last.duration_ms != null && (
                                  <span style={{ color: TEXT_TERTIARY }}> ({Math.round(last.duration_ms / 1000)}ש׳)</span>
                                )}
                              </span>
                            ) : '—'}
                          </td>
                          <td style={tdStyle}>
                            <span style={{ color, fontWeight: 700 }}>{label}</span>
                            {last && last.error && (
                              <div style={{ fontSize: 11, color: COLOR_DANGER, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {last.error}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ fontWeight: 700, marginBottom: 8 }}>סגירות הקרנות / Final Scrape צפוי</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                      <th style={thStyle}>סרט</th>
                      <th style={thStyle}>קולנוע</th>
                      <th style={thStyle}>אולם</th>
                      <th style={thStyle}>שעת הקרנה</th>
                      <th style={thStyle}>חלון Final</th>
                      <th style={thStyle}>סריקה אחרונה צפויה</th>
                      <th style={thStyle}>מצב</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closing.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: TEXT_TERTIARY }}>
                          אין הקרנות פעילות להיום שטרם עברו final scrape.
                        </td>
                      </tr>
                    ) : (
                      closing.slice(0, 80).map((c) => (
                        <tr key={c.screening_id} style={{ borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                          <td style={tdStyle}>{c.movie}</td>
                          <td style={tdStyle}>{c.cinema}</td>
                          <td style={tdStyle}>{c.hall || '-'}</td>
                          <td style={tdStyle}>{formatIsraelTime(c.showtime)}</td>
                          <td style={tdStyle}>
                            {formatIsraelTime(c.final_window_start)}
                            {' - '}
                            {formatIsraelTime(c.final_window_end)}
                          </td>
                          <td style={tdStyle}>
                            {formatIsraelTime(c.last_final_scan_time)}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              fontWeight: 700,
                              color: c.status === 'eligible_now' ? COLOR_OK : c.status === 'waiting_window' ? '#0ea5e9' : 'var(--faint)',
                            }}>
                              {c.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ── Hierarchical gallery component ───────────────────────────────────────

function DebugScreenshotsGallery() {
  const [expanded, setExpanded] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedChains, setExpandedChains] = useState({});
  const [expandedMovies, setExpandedMovies] = useState({});

  const loadScreenshots = useCallback(() => {
    setLoading(true);
    fetchDebugScreenshots()
      .then(setScreenshots)
      .catch(() => setScreenshots([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (expanded) loadScreenshots();
  }, [expanded, loadScreenshots]);

  const handleClear = () => {
    if (!window.confirm('למחוק את כל הסקרינשוטים?')) return;
    clearDebugScreenshots()
      .then(() => setScreenshots([]))
      .catch(() => {});
  };

  // Group screenshots into hierarchy: chain -> group -> items
  // Group by movie if available, otherwise by branch, otherwise "ניווט"
  const grouped = {};
  for (const s of screenshots) {
    const parsed = parseScreenshotFilename(s.filename);
    const chainKey = parsed.chain || 'unknown';

    // Build a descriptive group key
    let groupKey;
    if (parsed.movie) {
      // Has a movie — group by movie name (with branch as sub-info shown per item)
      groupKey = parsed.movie;
    } else if (parsed.branch && parsed.step && ['page', 'branch', 'dropdown'].includes(parsed.step)) {
      // Navigation screenshot — group under "ניווט" (navigation)
      groupKey = 'ניווט';
    } else if (parsed.branch) {
      groupKey = parsed.branch;
    } else {
      groupKey = 'ניווט';
    }

    if (!grouped[chainKey]) grouped[chainKey] = {};
    if (!grouped[chainKey][groupKey]) grouped[chainKey][groupKey] = [];
    grouped[chainKey][groupKey].push({ ...s, parsed });
  }

  const toggleChain = (chain) => {
    setExpandedChains(prev => ({ ...prev, [chain]: !prev[chain] }));
  };

  const toggleMovie = (key) => {
    setExpandedMovies(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div style={{
      background: NEU_BG,
      borderRadius: 12,
      border: `1px solid ${BORDER_SUBTLE}`,
      boxShadow: NEU_RAISED,
      marginBottom: 24,
      overflow: 'hidden',
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '14px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            סקרינשוטים Debug
          </span>
          {screenshots.length > 0 && (
            <span style={{
              background: '#dc262622',
              color: COLOR_DANGER,
              padding: '2px 8px',
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 600,
            }}>
              {screenshots.length}
            </span>
          )}
        </div>
	        <span style={{ color: TEXT_TERTIARY, fontSize: 18 }}>
          {expanded ? '\u25B2' : '\u25BC'}
        </span>
      </div>

      {expanded && (
        <div style={{ padding: '0 20px 16px' }}>
          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button
              onClick={() => window.open(getDebugScreenshotUrl(), '_blank')}
              style={screenshotBtnStyle}
            >
              אתר ראשי
            </button>
            <button
              onClick={() => window.open(getDebugScreenshotTicketsUrl(), '_blank')}
              style={screenshotBtnStyle}
            >
              כרטיסים (אחרון)
            </button>
            <button onClick={loadScreenshots} style={screenshotBtnStyle}>
              {loading ? '...' : 'רענן'}
            </button>
            {screenshots.length > 0 && (
              <button onClick={handleClear} style={{
                ...screenshotBtnStyle,
                background: 'rgba(239,68,68,0.15)',
                color: COLOR_DANGER,
                borderColor: COLOR_DANGER,
              }}>
                נקה הכל
              </button>
            )}
          </div>

          {/* Hierarchical tree */}
          {screenshots.length === 0 ? (
	            <div style={{ color: TEXT_TERTIARY, fontSize: 14, textAlign: 'center', padding: 16 }}>
              {loading ? 'טוען...' : 'אין סקרינשוטים. הרץ סריקה עם ticket updates.'}
            </div>
          ) : (
            Object.entries(grouped).map(([chainKey, movies]) => {
              const chainLabel = CHAIN_LABELS[chainKey] || chainKey;
              const chainColor = CHAIN_COLORS[chainKey] || TEXT_TERTIARY;
              const chainOpen = expandedChains[chainKey] !== false; // open by default
              const chainCount = Object.values(movies).reduce((sum, arr) => sum + arr.length, 0);

              return (
                <div key={chainKey} style={{ marginBottom: 8 }}>
                  {/* Chain header */}
                  <div
                    onClick={() => toggleChain(chainKey)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      background: `${chainColor}15`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      userSelect: 'none',
                      border: `1px solid ${chainColor}33`,
                    }}
                  >
	                      <span style={{ color: TEXT_TERTIARY, fontSize: 14 }}>
                      {chainOpen ? '\u25BC' : '\u25B6'}
                    </span>
                    <span style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: chainColor,
                    }}>
                      {chainLabel}
                    </span>
                    <span style={{
                      background: `${chainColor}22`,
                      color: chainColor,
                      padding: '1px 8px',
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 600,
                    }}>
                      {chainCount}
                    </span>
                  </div>

                  {chainOpen && (
                    <div style={{ paddingRight: 16, marginTop: 4 }}>
                      {Object.entries(movies).map(([movieKey, items]) => {
                        const fullKey = `${chainKey}__${movieKey}`;
                        const movieOpen = expandedMovies[fullKey] !== false;

                        return (
                          <div key={fullKey} style={{ marginBottom: 4 }}>
                            {/* Movie header */}
                            <div
                              onClick={() => toggleMovie(fullKey)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '6px 10px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                borderRadius: 6,
                              }}
                            >
	                              <span style={{ color: TEXT_TERTIARY, fontSize: 12 }}>
                                {movieOpen ? '\u25BC' : '\u25B6'}
                              </span>
	                              <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY }}>
                                {movieKey}
                              </span>
                              <span style={{
	                                color: TEXT_TERTIARY,
                                fontSize: 12,
                              }}>
                                ({items.length})
                              </span>
                            </div>

                            {movieOpen && (
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                                gap: 8,
                                paddingRight: 20,
                                marginTop: 4,
                                marginBottom: 8,
                              }}>
                                {items.map((s) => (
                                  <div
                                    key={s.filename}
                                    title={s.filename}
                                    onClick={() => window.open(getDebugScreenshotFileUrl(s.filename), '_blank')}
                                    style={{
	                                      background: SURF_ALT,
                                      borderRadius: 8,
                                      padding: '10px 14px',
                                      cursor: 'pointer',
	                                      border: `1px solid ${BORDER_SUBTLE}`,
                                      transition: 'border-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.borderColor = chainColor}
	                                    onMouseLeave={(e) => e.currentTarget.style.borderColor = BORDER_SUBTLE}
                                  >
                                    {/* Step label + time */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
	                                      <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>
                                        {getStepLabel(s.parsed.step)}
                                      </span>
                                      {s.parsed.time && (
                                        <span style={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: COLOR_DANGER,
                                          fontFamily: 'monospace',
                                        }}>
                                          {s.parsed.time}
                                        </span>
                                      )}
                                    </div>
                                    {/* Branch + movie info */}
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                                      {s.parsed.branch && (
                                        <span style={{
                                          fontSize: 11,
                                          color: chainColor,
                                          background: `${chainColor}15`,
                                          padding: '1px 6px',
                                          borderRadius: 4,
                                          fontWeight: 600,
                                        }}>
                                          {s.parsed.branch}
                                        </span>
                                      )}
                                      {s.parsed.movie && (
                                        <span style={{
                                          fontSize: 11,
	                                          color: TEXT_SECONDARY,
                                        }}>
                                          {s.parsed.movie}
                                        </span>
                                      )}
                                    </div>
	                                    <div style={{ fontSize: 11, color: TEXT_TERTIARY }}>
                                      {s.size_kb} KB · {new Date(s.created_at * 1000).toLocaleTimeString('he-IL', { timeZone: ISRAEL_TZ })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}


function ProgressCard({ progress, chainName }) {
  const { phase, current, total, detail } = progress;
  const pct = total > 0 ? Math.round((current / total) * 100) : null;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)',
      borderRadius: 12,
      padding: '20px 24px',
      border: '1px solid #bfdbfe',
      marginBottom: 24,
      boxShadow: NEU_RAISED,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: ACCENT,
            animation: 'pulse-dot 1.5s ease-in-out infinite',
          }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY }}>{phase}</span>
          {chainName && (
            <span style={{
              fontSize: 12,
              color: TEXT_SECONDARY,
              background: BORDER_SUBTLE,
              padding: '2px 8px',
              borderRadius: 6,
            }}>
              {chainName}
            </span>
          )}
        </div>
        {pct !== null && (
          <span style={{ fontSize: 22, fontWeight: 800, color: ACCENT }}>
            {pct}%
          </span>
        )}
      </div>

      {/* Progress bar */}
      {pct !== null && (
        <div style={{
          width: '100%',
          height: 12,
          borderRadius: 6,
          background: SURF_ALT,
          overflow: 'hidden',
          marginBottom: 10,
        }}>
          <div style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: 6,
            background: 'linear-gradient(90deg, #1d4ed8, #2563eb, #60a5fa)',
            backgroundSize: '40px 40px',
            backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.1) 75%, transparent 75%, transparent)',
            animation: 'progress-stripe 1s linear infinite',
            transition: 'width 0.5s ease',
          }} />
        </div>
      )}

      {/* Detail line */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 14, color: TEXT_SECONDARY }}>
          {detail && <span style={{ color: TEXT_PRIMARY }}>{detail}</span>}
        </span>
        {total > 0 && (
          <span style={{ fontSize: 13, color: TEXT_TERTIARY }}>
            {current} / {total}
          </span>
        )}
        {total === 0 && current > 0 && (
          <span style={{ fontSize: 13, color: TEXT_TERTIARY }}>
            {current} עובדו
          </span>
        )}
      </div>
    </div>
  );
}


const triggerBtnStyle = {
  padding: '12px 24px',
  borderRadius: 8,
  border: 'none',
  fontSize: 15,
  fontWeight: 600,
  fontFamily: 'Heebo, sans-serif',
  color: '#fff',
  transition: 'all 0.2s',
};

const screenshotBtnStyle = {
  padding: '10px 20px',
  borderRadius: 8,
  border: `1px solid ${BORDER_SUBTLE}`,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: 'Heebo, sans-serif',
  background: 'var(--danger-bg)',
  color: TEXT_PRIMARY,
  transition: 'all 0.2s',
};

const thStyle = {
  textAlign: 'right',
  padding: '10px 16px',
  color: TEXT_SECONDARY,
  fontWeight: 600,
  fontSize: 13,
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 16px',
  color: TEXT_PRIMARY,
  whiteSpace: 'nowrap',
};

export default ScrapePage;
